import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Typography,
  Box,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  Tooltip
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import { API_BASE_URL } from '../config';
import { authenticatedFetch, apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import TreatmentSelectorWithFilters from './TreatmentSelectorWithFilters';

const CATEGORIES = [
  'Wellness',
  'Detox',
  'Recovery',
  'Therapeutic',
  'Fitness',
  'Ayurveda',
  'Luxury',
  'Other'
];

const RetreatPackageBuilder = ({ open, onClose, packageToEdit, onSaved }) => {
  // Package details
  const [packageName, setPackageName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('Wellness');
  const [nights, setNights] = useState(7); // Number of nights (7, 14, 21, etc.)
  const [externalPackageId, setExternalPackageId] = useState(''); // AIIMS package ID for mapping
  
  // Treatments (flat list with day_number for each)
  const [treatments, setTreatments] = useState([]);
  
  // Available treatments from API
  const [availableTreatments, setAvailableTreatments] = useState([]);
  
  // Daily inclusions from API
  const [dailyInclusions, setDailyInclusions] = useState([]);
  
  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Cost tracking
  const [totalCost, setTotalCost] = useState(0);

  // Load treatments and daily inclusions on mount
  useEffect(() => {
    const init = async () => {
      if (open) {
        const treatments = await loadTreatments(); // Get treatments
        const inclusions = await loadDailyInclusions(); // Get daily inclusions
        
        if (packageToEdit) {
          loadPackageData(packageToEdit, treatments, inclusions); // Pass both
        } else {
          resetForm();
        }
      }
    };
    init();
  }, [open, packageToEdit]);

  // Calculate total cost whenever treatments change
  useEffect(() => {
    const total = treatments.reduce((sum, treatment) => {
      const price = parseFloat(treatment.base_price) || 0;
      console.log(`💵 ${treatment.treatment_name || 'Unnamed'}: ₹${price} (is_daily_inclusion: ${treatment.is_daily_inclusion})`);
      return sum + price;
    }, 0);
    console.log(`💰 Total Package Cost: ₹${total}`);
    setTotalCost(total);
  }, [treatments]);

  const loadTreatments = async () => {
    try {
      const response = await apiGet('/api/admin/treatments');
      if (response.ok) {
        const treatments = await response.json();
        const treatmentsArray = Array.isArray(treatments) ? treatments : [];
        setAvailableTreatments(treatmentsArray);
        return treatmentsArray; // Return the treatments for immediate use
      }
    } catch (err) {
      console.error('Failed to load treatments:', err);
      setError('Failed to load treatments');
    }
    return []; // Return empty array on error
  };

  const loadDailyInclusions = async () => {
    try {
      const response = await apiGet('/api/packages/daily-inclusions');
      if (response.ok) {
        const inclusions = await response.json();
        const inclusionsArray = Array.isArray(inclusions) ? inclusions : [];
        console.log('✅ Loaded Daily Inclusions:', inclusionsArray);
        setDailyInclusions(inclusionsArray);
        return inclusionsArray; // Return for immediate use
      } else {
        console.error('❌ Failed to load daily inclusions:', response.status);
      }
    } catch (err) {
      console.error('❌ Failed to load daily inclusions:', err);
    }
    return []; // Return empty array on error
  };

  const loadPackageData = async (pkg, treatmentsArray = null, dailyInclusionsArray = null) => {
    try {
      setLoading(true);
      const response = await apiGet(`/api/retreat-packages/${pkg.id}`);
      if (response.ok) {
        const data = await response.json();
        
        setPackageName(data.name);
        setDescription(data.description || '');
        setCategory(data.category || 'Wellness');
        setNights(data.nights || 7);
        setExternalPackageId(data.external_package_id || '');
        
        // Load items with pricing
        const treatments = treatmentsArray || availableTreatments;
        const inclusions = dailyInclusionsArray || dailyInclusions;
        
        // Calculate average Daily Inclusion price if needed
        let dailyInclusionPrice = 0;
        if (inclusions.length > 0) {
          const totalPrice = inclusions.reduce((sum, di) => {
            // API returns treatmentName in camelCase
            const treatmentName = di.treatmentName || di.treatment_name;
            // Case-insensitive matching since all treatments will be uppercase
            const treatment = treatments.find(t => 
              t.name.toUpperCase() === treatmentName.toUpperCase()
            );
            return sum + (treatment ? (treatment.base_price || treatment.tier_value || 0) : 0);
          }, 0);
          dailyInclusionPrice = totalPrice / inclusions.length;
          console.log(`📦 Loading package - Daily Inclusion average price: ₹${dailyInclusionPrice}`);
        }
        
        const treatmentsList = data.items.map(item => {
          // Case-insensitive matching since all treatments will be uppercase
          const treatmentDetails = treatments.find(t => 
            t.name.toUpperCase() === item.treatment_name.toUpperCase()
          );
          const isDaily = item.is_daily_inclusion || false;
          
          return {
            day_number: item.day_number,
            treatment_name: item.treatment_name,
            duration: item.duration,
            order_in_day: item.order_in_day,
            is_daily_inclusion: isDaily,
            base_price: isDaily ? dailyInclusionPrice : (treatmentDetails ? (treatmentDetails.base_price || treatmentDetails.tier_value || 0) : 0),
            tier_level: isDaily ? 'T5' : (treatmentDetails?.tier_level || null)
          };
        });
        
        console.log('📦 Loaded treatments:', treatmentsList);
        setTreatments(treatmentsList);
      }
    } catch (err) {
      console.error('Failed to load package:', err);
      setError('Failed to load package data');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setPackageName('');
    setDescription('');
    setCategory('Wellness');
    setNights(7);
    setExternalPackageId('');
    setTreatments([]);
    setError(null);
  };

  const addTreatment = () => {
    setTreatments([...treatments, {
      day_number: 1,
      treatment_name: '',
      duration: 50,
      order_in_day: 1,
      is_daily_inclusion: false,
      base_price: 0,
      tier_level: null
    }]);
  };

  const removeTreatment = (treatmentIndex) => {
    setTreatments(treatments.filter((_, idx) => idx !== treatmentIndex));
  };

  const updateTreatment = (treatmentIndex, field, value) => {
    const newTreatments = [...treatments];
    
    if (field === 'treatment_name') {
      // Find treatment details - case-insensitive matching
      const treatment = availableTreatments.find(t => 
        t.name.toUpperCase() === value.toUpperCase()
      );
      if (treatment) {
        newTreatments[treatmentIndex] = {
          ...newTreatments[treatmentIndex],
          treatment_name: value,
          duration: treatment.duration || 50,
          base_price: treatment.base_price || treatment.tier_value || 0,
          tier_level: treatment.tier_level || null
        };
      } else {
        newTreatments[treatmentIndex][field] = value;
      }
    } else if (field === 'is_daily_inclusion') {
      // When checking Daily Inclusion, set treatment name to "Daily Inclusion"
      newTreatments[treatmentIndex].is_daily_inclusion = value;
      if (value) {
        newTreatments[treatmentIndex].treatment_name = 'Daily Inclusion';
        newTreatments[treatmentIndex].tier_level = 'T5'; // Daily Inclusions are T5
        
        // Calculate average price from daily inclusions list
        console.log('📊 Daily Inclusions:', dailyInclusions);
        console.log('📊 Available Treatments:', availableTreatments.length);
        
        if (dailyInclusions.length > 0) {
          const prices = dailyInclusions.map(di => {
            // API returns treatmentName in camelCase
            const treatmentName = di.treatmentName || di.treatment_name;
            // Case-insensitive matching since all treatments will be uppercase
            const treatment = availableTreatments.find(t => 
              t.name.toUpperCase() === treatmentName.toUpperCase()
            );
            const price = treatment ? (treatment.base_price || treatment.tier_value || 0) : 0;
            console.log(`  - ${treatmentName}: ₹${price}`);
            return price;
          });
          
          const totalPrice = prices.reduce((sum, price) => sum + price, 0);
          const avgPrice = totalPrice / dailyInclusions.length;
          
          console.log(`💰 Total: ₹${totalPrice}, Average: ₹${avgPrice}`);
          newTreatments[treatmentIndex].base_price = avgPrice;
        } else {
          console.warn('⚠️ No daily inclusions found!');
          newTreatments[treatmentIndex].base_price = 0;
        }
        
        // AUTO-ASSIGN DI TO ALL UNASSIGNED DAYS
        // Get all days that should exist (1 to nights + 1)
        const totalDays = nights + 1;
        const daysWithDI = new Set();
        
        // Find which days already have DI
        newTreatments.forEach((t, idx) => {
          if (t.is_daily_inclusion && t.day_number) {
            daysWithDI.add(t.day_number);
          }
        });
        
        console.log(`📅 Days with DI already:`, Array.from(daysWithDI));
        console.log(`📅 Total days in package: ${totalDays}`);
        
        // Find days without DI
        const daysWithoutDI = [];
        for (let day = 1; day <= totalDays; day++) {
          if (!daysWithDI.has(day)) {
            daysWithoutDI.push(day);
          }
        }
        
        console.log(`📅 Days without DI:`, daysWithoutDI);
        
        // If there are unassigned days and this is the first DI being added,
        // add DI treatments for all unassigned days
        if (daysWithoutDI.length > 0 && daysWithDI.size <= 1) {
          console.log(`🔄 Auto-adding DI to ${daysWithoutDI.length} unassigned days`);
          
          // Get the next order_in_day for each day
          const maxOrderByDay = {};
          newTreatments.forEach(t => {
            if (t.day_number) {
              maxOrderByDay[t.day_number] = Math.max(maxOrderByDay[t.day_number] || 0, t.order_in_day || 0);
            }
          });
          
          // Add DI for each unassigned day (except the current one which is already added)
          daysWithoutDI.forEach(dayNum => {
            if (dayNum !== newTreatments[treatmentIndex].day_number) {
              newTreatments.push({
                day_number: dayNum,
                treatment_name: 'Daily Inclusion',
                duration: newTreatments[treatmentIndex].duration || 50,
                order_in_day: (maxOrderByDay[dayNum] || 0) + 1,
                is_daily_inclusion: true,
                base_price: newTreatments[treatmentIndex].base_price || 0,
                tier_level: 'T5'
              });
              console.log(`✅ Added DI to day ${dayNum}`);
            }
          });
          
          // Show notification to user
          alert(`Daily Inclusion auto-assigned to ${daysWithoutDI.length} days (days without DI)`);
        }
      } else {
        // If unchecking, clear the treatment name so user can select
        if (newTreatments[treatmentIndex].treatment_name === 'Daily Inclusion') {
          newTreatments[treatmentIndex].treatment_name = '';
          newTreatments[treatmentIndex].base_price = 0;
          newTreatments[treatmentIndex].tier_level = null;
        }
      }
    } else {
      newTreatments[treatmentIndex][field] = value;
    }
    
    setTreatments(newTreatments);
  };

  const handleSave = async () => {
    // Validation
    if (!packageName.trim()) {
      setError('Package name is required');
      return;
    }
    
    if (nights < 1 || nights > 50) {
      setError('Nights must be between 1 and 50');
      return;
    }
    
    // Check if at least one treatment exists
    if (treatments.length === 0) {
      setError('Add at least one treatment to the package');
      return;
    }
    
    // Check if all treatments are selected and have days
    const allTreatmentsValid = treatments.every(t => t.treatment_name && t.day_number);
    if (!allTreatmentsValid) {
      setError('Please select a treatment and day for all items');
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      // Build items array - group by day and add order_in_day
      const treatmentsByDay = {};
      treatments.forEach(t => {
        if (!treatmentsByDay[t.day_number]) {
          treatmentsByDay[t.day_number] = [];
        }
        treatmentsByDay[t.day_number].push(t);
      });
      
      const items = [];
      Object.keys(treatmentsByDay).sort((a, b) => parseInt(a) - parseInt(b)).forEach(dayNum => {
        treatmentsByDay[dayNum].forEach((treatment, idx) => {
          items.push({
            day_number: parseInt(dayNum),
            treatment_name: treatment.treatment_name,
            duration: treatment.duration,
            order_in_day: idx + 1,
            is_daily_inclusion: treatment.is_daily_inclusion
          });
        });
      });
      
      const payload = {
        name: packageName,
        description: description,
        category: category,
        nights: parseInt(nights),
        external_package_id: externalPackageId || null,
        created_by: 'Admin', // TODO: Get from user session
        created_by_role: 'admin',
        is_active: true,
        items: items
      };
      
      let response;
      if (packageToEdit) {
        response = await apiPut(`/api/retreat-packages/${packageToEdit.id}`, payload);
      } else {
        response = await apiPost('/api/retreat-packages', payload);
      }
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save package');
      }
      
      const result = await response.json();
      
      if (packageToEdit && result.bsynapse_synced === false && result.bsynapse_sync_error) {
        alert(
          `Package "${packageName}" saved locally, but AIIMS sync failed:\n${result.bsynapse_sync_error}\n\n` +
          'Ensure External Package ID (AIIMS _id) is set on the package.'
        );
      } else {
        alert(packageToEdit 
          ? `Package "${packageName}" updated successfully!${result.bsynapse_synced ? ' AIIMS synced.' : ''}`
          : `Package "${packageName}" created successfully!\n\n${items.length} treatments across ${nights} nights`
        );
      }
      
      if (onSaved) {
        onSaved(result);
      }
      
      onClose();
      resetForm();
      
    } catch (err) {
      console.error('Error saving package:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { 
          height: '90vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: '#E4C984', 
        color: '#171717',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
          {packageToEdit ? 'Edit Retreat Package' : 'Retreat Package Builder'}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, pt: 2, overflowY: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Two-Column Layout: 30% Left (Details) / 70% Right (Treatments) */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '30% 68%', gap: '2%', mt: 2 }}>
              
              {/* LEFT PANEL - Package Details */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#374151' }}>
                    PACKAGE DETAILS
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Package Name *"
                      value={packageName}
                      onChange={(e) => setPackageName(e.target.value)}
                      placeholder="e.g., 7 Night Wellness Retreat"
                      variant="outlined"
                    />
                    
                    <TextField
                      fullWidth
                      label="Nights *"
                      type="number"
                      value={nights}
                      onChange={(e) => setNights(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                      inputProps={{ min: 1, max: 50 }}
                      variant="outlined"
                      helperText="Number of nights (1-50)"
                    />
                    
                    <TextField
                      fullWidth
                      label="External Package ID"
                      value={externalPackageId}
                      onChange={(e) => setExternalPackageId(e.target.value)}
                      placeholder="e.g., 171013598082850"
                      variant="outlined"
                      helperText="AIIMS program ID for auto-matching with guest packages (optional)"
                    />
                    
                    <FormControl fullWidth>
                      <InputLabel>Category</InputLabel>
                      <Select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        label="Category"
                      >
                        {CATEGORIES.map(cat => (
                          <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      fullWidth
                      label="Description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Brief description..."
                      multiline
                      rows={4}
                      variant="outlined"
                    />
                  </Box>
                </Box>

                {/* Info Box */}
                <Paper sx={{ p: 2, backgroundColor: '#fffbeb', border: '1px solid #E4C984' }}>
                  <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 600 }}>
                    💡 Daily Inclusion
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Check "Daily Inclusion" to use rotating treatments from the master list. 
                    These treatments rotate automatically during scheduling (6 treatments in rotation).
                  </Typography>
                </Paper>
              </Box>

              {/* RIGHT PANEL - Treatments Only */}
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#374151' }}>
                  TREATMENTS BY DAY
                </Typography>

                {/* Treatments List */}
                <Box sx={{ mb: 1, pt: 1 }}>
                  {treatments.length === 0 ? (
                    <Alert 
                      severity="info" 
                      sx={{ 
                        fontSize: '0.875rem',
                        backgroundColor: '#fffbeb',
                        color: '#78350f',
                        '& .MuiAlert-icon': {
                          color: '#E4C984'
                        }
                      }}
                    >
                      No treatments added. Click "Add Treatment" below to start.
                    </Alert>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {treatments.map((treatment, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '70px 50px 1fr 100px 80px 40px',
                            gap: 1,
                            alignItems: 'center',
                            p: 1.5,
                            border: '1px solid #e5e7eb',
                            borderRadius: 1,
                            backgroundColor: treatment.is_daily_inclusion ? '#fffbeb' : 'white'
                          }}
                        >
                          {/* Day Number */}
                          <TextField
                            size="small"
                            label="Day"
                            type="number"
                            value={treatment.day_number || 1}
                            onChange={(e) => updateTreatment(index, 'day_number', parseInt(e.target.value) || 1)}
                            inputProps={{ min: 1, max: nights + 1 }}
                            variant="outlined"
                          />

                          {/* DI Checkbox */}
                          <Tooltip title={
                            dailyInclusions.length === 0 
                              ? "Loading daily inclusions..." 
                              : "Daily Inclusion - Auto-rotates from master list"
                          }>
                            <Box 
                              onClick={() => {
                                if (dailyInclusions.length === 0) {
                                  alert('Please wait, loading daily inclusions...');
                                  return;
                                }
                                updateTreatment(index, 'is_daily_inclusion', !treatment.is_daily_inclusion);
                              }}
                              sx={{ 
                                display: 'flex', 
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '40px',
                                border: treatment.is_daily_inclusion ? '2px solid #E4C984' : '1px solid #d1d5db',
                                borderRadius: '4px',
                                backgroundColor: treatment.is_daily_inclusion ? '#fffbeb' : 'white',
                                cursor: dailyInclusions.length === 0 ? 'not-allowed' : 'pointer',
                                opacity: dailyInclusions.length === 0 ? 0.5 : 1,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  borderColor: treatment.is_daily_inclusion ? '#d4b874' : '#9ca3af',
                                  backgroundColor: treatment.is_daily_inclusion ? '#fef3c7' : '#f9fafb'
                                }
                              }}
                            >
                              {treatment.is_daily_inclusion && (
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                    color: '#E4C984',
                                    mr: 0.5
                                  }}
                                >
                                  ✓
                                </Typography>
                              )}
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  fontSize: '0.75rem',
                                  fontWeight: treatment.is_daily_inclusion ? 600 : 400,
                                  color: treatment.is_daily_inclusion ? '#E4C984' : '#6b7280'
                                }}
                              >
                                DI
                              </Typography>
                            </Box>
                          </Tooltip>

                          {/* Treatment Selection */}
                          <TreatmentSelectorWithFilters
                            treatments={availableTreatments}
                            selectedTreatment={treatment.treatment_name}
                            onSelect={(selectedTreatment) => {
                              if (selectedTreatment) {
                                updateTreatment(index, 'treatment_name', selectedTreatment.name);
                              }
                            }}
                            disabled={treatment.is_daily_inclusion}
                            label={treatment.is_daily_inclusion ? "Daily Inclusion (Auto-Rotated)" : "Select Treatment"}
                            placeholder={treatment.is_daily_inclusion ? "Daily Inclusion" : "Search treatments..."}
                          />

                          {/* Duration */}
                          <TextField
                            size="small"
                            label="Minutes"
                            type="number"
                            value={treatment.duration}
                            onChange={(e) => updateTreatment(index, 'duration', parseInt(e.target.value) || 50)}
                            inputProps={{ min: 5, max: 240, step: 5 }}
                            variant="outlined"
                          />

                          {/* Price */}
                          <Chip
                            label={`₹${(treatment.base_price || 0).toFixed(0)}`}
                            size="small"
                            sx={{ 
                              backgroundColor: treatment.is_daily_inclusion ? '#E4C984' : '#E4C984', 
                              color: '#171717',
                              fontWeight: 600
                            }}
                          />

                          {/* Delete Button */}
                          <IconButton
                            size="small"
                            onClick={() => removeTreatment(index)}
                            sx={{ color: '#dc2626' }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>

                {/* Add Treatment Button - Outside scroll area */}
                <Button
                  variant="outlined"
                  onClick={addTreatment}
                  startIcon={<AddCircleOutlineIcon />}
                  sx={{
                    borderColor: '#E4C984',
                    color: '#E4C984',
                    '&:hover': {
                      borderColor: '#d4b874',
                      backgroundColor: 'rgba(228, 201, 132, 0.1)'
                    }
                  }}
                >
                  Add Treatment
                </Button>
              </Box>
              {/* End RIGHT PANEL */}

            </Box>
            {/* End Two-Column Grid */}

            {/* Summary Info */}
            {treatments.length > 0 && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '2px solid #e5e7eb' }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    backgroundColor: '#fffbeb',
                    border: '2px solid #E4C984'
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 3, justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Treatments</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>{treatments.length}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Inclusions</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#E4C984' }}>
                          {treatments.filter(t => t.is_daily_inclusion).length}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Days</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {Math.max(...treatments.map(t => t.day_number || 1))}
                        </Typography>
                      </Box>
                      
                      {/* Tier Breakdown */}
                      {(() => {
                        const tierCounts = {};
                        treatments.forEach(t => {
                          if (t.is_daily_inclusion) {
                            // Daily Inclusions are T5
                            tierCounts['T5'] = (tierCounts['T5'] || 0) + 1;
                          } else {
                            // Regular treatments - get tier from treatment data or stored tier_level
                            // Case-insensitive matching
                            const tier = t.tier_level || 
                                        (availableTreatments.find(at => 
                                          at.name.toUpperCase() === t.treatment_name.toUpperCase()
                                        )?.tier_level);
                            if (tier) {
                              tierCounts[tier] = (tierCounts[tier] || 0) + 1;
                            }
                          }
                        });
                        
                        return Object.keys(tierCounts).sort().map(tier => (
                          <Box key={tier}>
                            <Typography variant="caption" color="text.secondary">{tier}</Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: tier === 'T5' ? '#E4C984' : 'inherit' }}>
                              {tierCounts[tier]}
                            </Typography>
                          </Box>
                        ));
                      })()}
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="caption" color="text.secondary">Total Package Cost</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#E4C984' }}>
                        ₹{totalCost.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, backgroundColor: '#f9fafb' }}>
        <Button onClick={onClose} disabled={saving}>
          CANCEL
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || loading}
          sx={{
            backgroundColor: '#E4C984',
            color: '#171717',
            fontWeight: 'bold',
            '&:hover': {
              backgroundColor: '#d4b874'
            }
          }}
        >
          {saving ? 'SAVING...' : 'SAVE PACKAGE'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RetreatPackageBuilder;

